import {
  type AnchorPoint,
  compareAnchorPoints,
  type DocumentOrder,
  documentPointOf,
  unanchoredRootIds,
} from "./reviewOrder";
import type { ThreadNode } from "./threads";
import { threadSize } from "./threads";

/**
 * Review-notes root ordering (review-notes enhancements §1). The server returns
 * comments FLAT ordered by `created_at`; `buildThreads` nests them preserving
 * that order. This module sorts the ROOT array (never the flat list, never a
 * reply array — replies stay in creation order under each root) by a
 * reviewer-chosen key.
 *
 * `citation` is the DEFAULT and means DOCUMENT ORDER — the order a reader meets
 * the notes walking the document top to bottom. It is computed from each note's
 * anchor by {@link documentPointOf}, NOT from the order the rows arrived in.
 *
 * ⛔ It used to be an identity comparator (`return () => 0`) over a list the
 * server had sorted by `created_at`, behind the label "Document order". The
 * label was false from the day it shipped, and the comment that excused it
 * ("until a server-derived `anchor_ord` ships…") kept it false. If a note's
 * position genuinely cannot be derived it goes to a NAMED, VISIBLE bucket at the
 * end ({@link unanchoredRoots}) — the one thing that must never happen again is
 * an option claiming an ordering the code does not perform.
 *
 * `modified-*` keys off `updated_at` (the content-last-changed time, bumped only
 * on an author body edit) and falls back to `created_at` when the server hasn't
 * populated it — so a never-edited note sorts by its creation time.
 */
export type ReviewSort =
  | "citation"
  | "created-asc"
  | "created-desc"
  | "modified-asc"
  | "modified-desc"
  | "replies-asc"
  | "replies-desc";

export const REVIEW_SORT_DEFAULT: ReviewSort = "citation";

export const REVIEW_SORT_OPTIONS: { value: ReviewSort; label: string }[] = [
  { value: "citation", label: "Document order" },
  { value: "created-desc", label: "Newest first" },
  { value: "created-asc", label: "Oldest first" },
  { value: "modified-desc", label: "Recently edited" },
  { value: "modified-asc", label: "Least recently edited" },
  { value: "replies-desc", label: "Most replies" },
  { value: "replies-asc", label: "Fewest replies" },
];

const VALID = new Set<ReviewSort>(REVIEW_SORT_OPTIONS.map((o) => o.value));

/** Narrow an arbitrary string to a `ReviewSort`, falling back to the default. */
export function coerceReviewSort(value: string | null | undefined): ReviewSort {
  return value && VALID.has(value as ReviewSort)
    ? (value as ReviewSort)
    : REVIEW_SORT_DEFAULT;
}

/** ms since epoch, or 0 for an unparseable/empty time (sorts such rows earliest). */
function ms(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** The content-modification time: `updated_at` when it holds a real time (a real
 *  edit), else the creation time (never-edited notes sort by when they were made).
 *
 *  An EMPTY `updated_at` is treated as ABSENT. `updated_at ?? created_at` does
 *  not catch `""` — an empty string is neither null nor undefined — so a server
 *  that emitted one made the note parse as epoch 0 and sort as the oldest thing
 *  in the rail, under a label that reads "Recently edited". */
function modifiedMs(node: ThreadNode): number {
  const c = node.comment;
  const edited = c.updated_at;
  return ms(edited && edited.length > 0 ? edited : c.created_at);
}

/** The number of replies under a root (every descendant of the thread). */
export function replyCount(node: ThreadNode): number {
  return threadSize(node) - 1;
}

/** The roots with no derivable document position — the honest bucket the rail
 *  and the gutter render under their own heading when sorting by document order.
 *  Re-exported here so both surfaces read ONE module. */
export function unanchoredRoots(
  roots: readonly ThreadNode[],
  order?: DocumentOrder | null,
): ReadonlySet<number> {
  return unanchoredRootIds(roots, order);
}

/**
 * A STABLE, NON-MUTATING sort of the root array by `sort`. Stability is
 * guaranteed by a decorate-sort-undecorate over the input index, so equal keys
 * preserve their incoming (creation) order.
 *
 * `order` is the read surface's live anchor resolution. It is OPTIONAL: without
 * it `citation` still orders by each note's RECORDED anchor, which is a real
 * document position (one paint stale), never creation order. Passing it is what
 * makes a MOVED note sort where it is NOW and an ORPHANED one drop to the
 * bucket.
 */
export function sortRoots(
  roots: ThreadNode[],
  sort: ReviewSort,
  order?: DocumentOrder | null,
): ThreadNode[] {
  const byDocument = sort === "citation";
  const decorated = roots.map((node, index) => ({
    node,
    index,
    point: byDocument ? documentPointOf(node.comment, order) : null,
  }));
  const cmp = byDocument ? null : comparatorFor(sort);
  decorated.sort((a, b) => {
    const c = cmp ? cmp(a.node, b.node) : comparePlacement(a.point, b.point);
    return c !== 0 ? c : a.index - b.index;
  });
  return decorated.map((d) => d.node);
}

/** Placed notes in reading order; unplaceable ones after ALL of them, together,
 *  in their incoming order (the index tie-break in `sortRoots` supplies that). */
function comparePlacement(a: AnchorPoint | null, b: AnchorPoint | null): number {
  if (a && b) return compareAnchorPoints(a, b);
  if (a) return -1;
  if (b) return 1;
  return 0;
}

function comparatorFor(
  sort: Exclude<ReviewSort, "citation">,
): (a: ThreadNode, b: ThreadNode) => number {
  switch (sort) {
    case "created-desc":
      return (a, b) => ms(b.comment.created_at) - ms(a.comment.created_at);
    case "modified-asc":
      return (a, b) => modifiedMs(a) - modifiedMs(b);
    case "modified-desc":
      return (a, b) => modifiedMs(b) - modifiedMs(a);
    case "replies-asc":
      return (a, b) =>
        replyCount(a) - replyCount(b) ||
        ms(a.comment.created_at) - ms(b.comment.created_at);
    case "replies-desc":
      return (a, b) =>
        replyCount(b) - replyCount(a) ||
        ms(a.comment.created_at) - ms(b.comment.created_at);
    default:
      // `created-asc`
      return (a, b) => ms(a.comment.created_at) - ms(b.comment.created_at);
  }
}

// ── persistence (§1.4) ──────────────────────────────────────────────────────
// GLOBAL per-reviewer preference: the chosen sort survives reload and applies
// across documents. localStorage is the source of truth.
const STORAGE_KEY = "elaborate.reviewNotes.sort";

export function loadReviewSort(): ReviewSort {
  try {
    return coerceReviewSort(
      globalThis.localStorage?.getItem(STORAGE_KEY) ?? null,
    );
  } catch {
    return REVIEW_SORT_DEFAULT;
  }
}

export function saveReviewSort(sort: ReviewSort): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, sort);
  } catch {
    // Private-mode / disabled storage — the choice simply doesn't persist.
  }
}
